import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import { plansData, getPlanTotal } from '../data';

const planAccent = {
  bronce: '#5C6BC0',
  plata: '#26A69A',
  oro: '#FFB300',
};

const InversionSection = () => {
  const theme = useTheme();

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          gutterBottom
          color="primary"
          align="center"
        >
          Planes de inversión
        </Typography>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Bronce, Plata y Oro — mensual
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 720, mx: 'auto' }}>
          Tres niveles de comercialización para Óptica Berlud: rotativa, noticiero, Digest, inserts y — en Plata y Oro —
          micros en estudio o con material de marca. Inversión referencial en USD/mes.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {plansData.map((plan) => {
          const breakdown = getPlanTotal(plan);
          const accent = planAccent[plan.id] || theme.palette.primary.main;

          return (
            <Grid item xs={12} md={4} key={plan.id} data-aos="fade-up">
              <Box
                component={Card}
                height="100%"
                display="flex"
                flexDirection="column"
                variant="outlined"
                sx={{
                  borderTop: 4,
                  borderColor: accent,
                  transition: 'all .2s ease-in-out',
                  boxShadow: plan.highlight ? 6 : 1,
                  transform: plan.highlight ? 'scale(1.02)' : 'none',
                  '&:hover': { boxShadow: 8 },
                }}
              >
                <Box
                  py={2}
                  px={2}
                  sx={{
                    bgcolor: accent,
                    color: theme.palette.common.white,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="overline" fontWeight={700} display="block">
                    {plan.tag}
                  </Typography>
                  <Typography variant="h3" fontWeight={900}>
                    ${breakdown.total}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.95 }}>
                    / mes · USD referencial
                  </Typography>
                </Box>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {plan.title}
                  </Typography>
                  <List dense disablePadding sx={{ flex: 1, mb: 2 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} disableGutters alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="textSecondary" display="block">
                      Desglose mensual
                    </Typography>
                    <Typography variant="body2">
                      Rotativa: <strong>${breakdown.rotativa}</strong>
                    </Typography>
                    <Typography variant="body2">
                      TNO Digest: <strong>${breakdown.digest}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Noticias TNO: <strong>${breakdown.noticiero}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Inserts redes: <strong>${breakdown.redes}</strong>
                    </Typography>
                    {breakdown.micros > 0 && (
                      <Typography variant="body2">
                        Micros en video: <strong>${breakdown.micros}</strong>
                      </Typography>
                    )}
                    <Typography variant="body2">
                      Web / aliados: <strong>${breakdown.web}</strong>
                    </Typography>
                    {breakdown.segmento > 0 && (
                      <Typography variant="body2">
                        En Sintonía con tu Salud: <strong>${breakdown.segmento}</strong>
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                    {plan.idealFor}
                  </Typography>
                </CardContent>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={4}>
        <Box
          component={Card}
          sx={{
            bgcolor: theme.palette.grey[100],
            borderLeft: 4,
            borderColor: theme.palette.primary.main,
          }}
        >
          <CardContent>
            <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
              Comparativa rápida
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Bronce ($290):</strong> 6 rotativas/día · 1 Digest · 1 mención noticiero · 2 inserts.
              {' '}
              <strong>Plata ($480):</strong> 12 rotativas/día · 2 Digest · 4 cintillos noticiero · 6 inserts · 2 micros (estudio o material de marca).
              {' '}
              <strong>Oro ($720):</strong> 12 rotativas + On-Demand · 4 Digest · noticiero + bloque salud · 12 inserts · 4 micros + vocero · En Sintonía con tu Salud.
            </Typography>
          </CardContent>
        </Box>
      </Box>

      <PriceVatDisclaimer />
    </Box>
  );
};

export default InversionSection;
